import { PROTOCOL_VERSION, type HiType } from './constants';
import {
  ENVELOPE_MAX_CIPHERTEXT_BYTES,
  decodeEnvelopeHeader,
  encodeEnvelopeHeader,
  envelopeSigningBytes,
  type EnvelopeHeader,
  type EnvelopeKind,
} from './envelope';
import { concatBytes, fromBase64Url, ownBytes, toBase64Url, utf8 } from './encoding';
import { peerMatchesId, type DeviceKeys, type PeerDevice } from './keys';
import { importEncryptionPublicKey, signBytes, verifySignature } from './signatures';

/** HKDF label for an envelope's content key. */
export const ENVELOPE_KEY_INFO = 'curentia/v1/envelope-key';
const GCM_TAG_BYTES = 16;
/** Largest plaintext that still fits the relay's ciphertext limit. */
export const ENVELOPE_MAX_PLAINTEXT_BYTES = ENVELOPE_MAX_CIPHERTEXT_BYTES - GCM_TAG_BYTES;

export type EnvelopeErrorCode =
  | 'too_large'
  | 'bad_recipient'
  | 'invalid_header'
  | 'wrong_recipient'
  | 'unknown_sender'
  | 'bad_signature'
  | 'decrypt_failed';

/** Why sealing or opening failed. Messages never include content. */
export class EnvelopeError extends Error {
  constructor(
    readonly code: EnvelopeErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'EnvelopeError';
  }
}

/**
 * The AES-256-GCM key for one envelope: HKDF-SHA-256 over the ECDH shared
 * secret, with both public keys in the info so the key is bound to this
 * sender key and this recipient.
 */
async function contentKey(
  sharedSecret: ArrayBuffer,
  ephemeralPublic: Uint8Array,
  recipientPublic: Uint8Array,
): Promise<CryptoKey> {
  const subtle = globalThis.crypto.subtle;
  const base = await subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  return subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: concatBytes(utf8(ENVELOPE_KEY_INFO), new Uint8Array([0]), ephemeralPublic, recipientPublic),
    },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export interface SealInput {
  kind: EnvelopeKind;
  sender: DeviceKeys;
  recipient: PeerDevice;
  plaintext: Uint8Array;
  /** Set when the sending device acts as this practice's record holder. */
  fromPractice?: string;
  /** Set when the recipient device is this practice's record holder. */
  toPractice?: string;
  consent?: string;
  hiType?: HiType;
  /** Only for fixed test vectors. Real envelopes always use fresh random values. */
  testing?: { id?: string; createdAt?: string; ephemeral?: CryptoKeyPair; iv?: Uint8Array };
}

export interface SealedEnvelope {
  id: string;
  header: EnvelopeHeader;
  headerBytes: Uint8Array<ArrayBuffer>;
  ciphertext: Uint8Array<ArrayBuffer>;
  signature: Uint8Array<ArrayBuffer>;
  /** Exactly what goes in a POST /api/envelopes list. */
  wire: { header: string; ciphertext: string; signature: string };
}

/**
 * Encrypts a payload for one recipient device and signs it. Each call makes a
 * fresh one-time ECDH key, so envelopes cannot be linked by key. The recipient's
 * keys must hash to its device ID, which ties the encryption to the device
 * confirmed in person.
 */
export async function sealEnvelope(input: SealInput): Promise<SealedEnvelope> {
  if (input.plaintext.length > ENVELOPE_MAX_PLAINTEXT_BYTES) {
    throw new EnvelopeError('too_large', 'The content is too large for one envelope');
  }
  if (!(await peerMatchesId(input.recipient))) {
    throw new EnvelopeError('bad_recipient', 'The recipient keys do not match the recipient device ID');
  }
  const subtle = globalThis.crypto.subtle;
  const recipientPublic = ownBytes(input.recipient.encryptionKey);
  const recipientKey = await importEncryptionPublicKey(recipientPublic);
  const ephemeral =
    input.testing?.ephemeral ??
    (await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']));
  const ephemeralPublic = new Uint8Array(await subtle.exportKey('raw', ephemeral.publicKey));
  const iv = ownBytes(input.testing?.iv ?? globalThis.crypto.getRandomValues(new Uint8Array(12)));

  const header: EnvelopeHeader = {
    v: PROTOCOL_VERSION,
    id: input.testing?.id ?? toBase64Url(globalThis.crypto.getRandomValues(new Uint8Array(16))),
    kind: input.kind,
    from: input.fromPractice ? { device: input.sender.id, practice: input.fromPractice } : { device: input.sender.id },
    to: input.toPractice ? { device: input.recipient.id, practice: input.toPractice } : { device: input.recipient.id },
    ...(input.consent ? { consent: input.consent } : {}),
    ...(input.hiType ? { hiType: input.hiType } : {}),
    epk: toBase64Url(ephemeralPublic),
    iv: toBase64Url(iv),
    createdAt: input.testing?.createdAt ?? new Date().toISOString(),
  };
  const headerBytes = encodeEnvelopeHeader(header);
  const shared = await subtle.deriveBits({ name: 'ECDH', public: recipientKey }, ephemeral.privateKey, 256);
  const key = await contentKey(shared, ephemeralPublic, recipientPublic);
  const ciphertext = new Uint8Array(
    await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: headerBytes, tagLength: 128 }, key, ownBytes(input.plaintext)),
  );
  const signature = await signBytes(input.sender.signingPrivate, envelopeSigningBytes(headerBytes, ciphertext));
  return {
    id: header.id,
    header,
    headerBytes,
    ciphertext,
    signature,
    wire: { header: toBase64Url(headerBytes), ciphertext: toBase64Url(ciphertext), signature: toBase64Url(signature) },
  };
}

export interface OpenInput {
  recipient: DeviceKeys;
  /** An envelope as the inbox returns it, with base64url fields. */
  envelope: { header: string; ciphertext: string; signature: string };
  /** The sending device's public keys. Pin them, for example from a QR check, rather than trusting the directory alone. */
  sender: PeerDevice;
}

/**
 * Checks and decrypts an envelope: canonical header, addressed to this device,
 * sent by the device whose keys were given, signature valid, and ciphertext
 * and header untouched. Throws an EnvelopeError naming the first failed check.
 * The caller should also refuse envelope IDs it has already seen.
 */
export async function openEnvelope(input: OpenInput): Promise<{ header: EnvelopeHeader; plaintext: Uint8Array<ArrayBuffer> }> {
  let headerBytes: Uint8Array<ArrayBuffer>;
  let header: EnvelopeHeader;
  let ciphertext: Uint8Array<ArrayBuffer>;
  let signature: Uint8Array<ArrayBuffer>;
  try {
    headerBytes = fromBase64Url(input.envelope.header);
    ciphertext = fromBase64Url(input.envelope.ciphertext);
    signature = fromBase64Url(input.envelope.signature);
    header = decodeEnvelopeHeader(headerBytes);
  } catch {
    throw new EnvelopeError('invalid_header', 'The envelope header is not valid');
  }
  if (header.to.device !== input.recipient.id) {
    throw new EnvelopeError('wrong_recipient', 'This envelope is for another device');
  }
  if (header.from.device !== input.sender.id || !(await peerMatchesId(input.sender))) {
    throw new EnvelopeError('unknown_sender', 'The sender keys do not match the sending device');
  }
  if (!(await verifySignature(input.sender.signingKey, signature, envelopeSigningBytes(headerBytes, ciphertext)))) {
    throw new EnvelopeError('bad_signature', 'The envelope signature does not match');
  }
  const subtle = globalThis.crypto.subtle;
  try {
    const ephemeralPublic = fromBase64Url(header.epk);
    const ephemeralKey = await importEncryptionPublicKey(ephemeralPublic);
    const shared = await subtle.deriveBits({ name: 'ECDH', public: ephemeralKey }, input.recipient.encryptionPrivate, 256);
    const key = await contentKey(shared, ephemeralPublic, input.recipient.encryptionPublic);
    const plaintext = await subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64Url(header.iv), additionalData: headerBytes, tagLength: 128 },
      key,
      ciphertext,
    );
    return { header, plaintext: new Uint8Array(plaintext) };
  } catch {
    throw new EnvelopeError('decrypt_failed', 'The envelope could not be decrypted');
  }
}

/** UTF-8 JSON bytes, for payloads such as FHIR documents. */
export function jsonBytes(value: unknown): Uint8Array<ArrayBuffer> {
  return utf8(JSON.stringify(value));
}

/** Parses UTF-8 JSON bytes. Throws on invalid UTF-8 or JSON. */
export function parseJsonBytes(bytes: Uint8Array): unknown {
  return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
}
